import type {MatchField, MatchType} from './journeys.js';
import type {Team} from './team.js';
import type {IngestSource} from './ingest.js';

export enum DestinationType {
  ZETA = 'zeta',
  MLB = 'mlb',
  NONMLB = 'nonmlb',
  CSV = 'csv',
  CRM_EXPORTS = 'CRM Exports',
}

export const DestinationWithMatchType = new Map([
  ['Linkedin Ads', 'linkedin_match_type'],
  ['DV360', 'dv360_match_type'],
  ['Google Adwords', 'google_ads_match_type'],
]);

export enum MatchFieldSet {
  DEFAULT = 'match_fields',
  TRIGGER_TYPE_SEND_EMAIL = 'match_fields__trigger_type_send_email',
}

type CampaignExportAction = {
  label: string;
  hint: string;
  value: string;
};
export interface DestinationConnection {
  id: number;
  destination_name: string;
  display_name?: string;
  match_field: string;
  created_at: string;
  updated_at: string;
  sync_frequency: string;
  team_id: number;
  export_type: string;
  source_match_field: string;
  source_connection_id: number;
  platform_type: string;
  platform_name: string;
  endpoint_id: string;
  is_enabled: boolean;
  active_audiences: number;
  icon: string;
  needs_reauthentication: boolean;
  config: object;
  categories?: string[];
  match_field_message?: string;
  [MatchFieldSet.DEFAULT]?: [MatchField][];
  [MatchFieldSet.TRIGGER_TYPE_SEND_EMAIL]?: [MatchField][];
  hide_match_fields_when?: ObjectPropertyMatchRules;
  name?: string;
  logo?: string;
  platform_credentials: object;
  is_mlb?: boolean;
  display_personalization_fields?: boolean;
  display_additional_fields?: boolean;
  disclosure_url?: string;
  platform_personalization_fields_custom_text: string;
  supports_campaign_triggers: boolean;
  campaign_export_actions: CampaignExportAction[];
  campaign_triggers_choose_sender: boolean;
  settings?: {
    linkedin_match_type?: MatchType;
    dv360_match_type?: MatchType;
    google_ads_match_type?: MatchType;
    marketing_cloud_business_unit_id?: string;
    marketing_cloud_folder?: string;
    pulse_point_advertiser?: string;
    pulse_point_product?: string;
    dv360_app_id?: string;
    google_ads_app_id?: string;
    qualtrics_list_type?: string;
    qualtrics_directory_id?: string;
  };
}

export type ObjectPropertyMatchRules = Record<string, string | boolean>;

export interface DestinationRecord {
  match_fields: [{label: string; value: string}][];
  [key: string]: unknown;
}

export interface DestinationConnectionSlim {
  id: number;
  icon: string;
  destination_name: string;
}

export type MatchFieldResponse = Array<DestinationConnection>;

export interface DestinationPlatformsResponse {
  providers: Provider[];
  provider_categories: string[];
  teams: Team[];
  import_sources: IngestSource[];
}

export interface Provider {
  readonly name: string;
  readonly display_name: string;
  readonly state_key?: string;
  readonly provider?: string;
  readonly provider_key?: string;
  readonly logo: string;
  readonly description?: string;
  readonly auth: ProviderAuth;
  readonly export_type?: string;
  readonly platform_type?: string;
  readonly categories?: string[]; // categories
  readonly display_personalization_fields?: boolean;
  readonly disclosure_url?: string;
  readonly loop_enabled: boolean;
  readonly form: Form;
}

interface ProviderAuth {
  required: boolean;
  after_submission?: boolean;
  url?: string;
}

export interface FormField {
  readonly label: string;
  readonly tooltip: string;
  readonly placeholder: string;
  readonly description?: string;
  readonly field_key: string; // For use with request payload to backend
  readonly field_type: string;
  readonly field_value: string;
  readonly rules:
    | FormFieldITextareaRules
    | FormFieldInputRules
    | FormFieldSelectRules
    | FormFieldHiddenRules
    | FormFieldToggleRules
    | FormFieldCheckboxRules;
  displayed?: boolean;
}

interface FormFieldInputRules {
  readonly _tag: 'input';
  readonly maxlength?: number;
  readonly minlength?: number;
  readonly pattern?: string;
  readonly required: boolean;
}

export interface FormFieldITextareaRules {
  readonly _tag: 'textarea';
  readonly maxlength?: number;
  readonly minlength?: number;
  readonly pattern?: string;
  readonly required: boolean;
}

export interface FormFieldSelectRules {
  readonly _tag: 'select';
  readonly options: FormFieldSelectOption[];
  readonly required: boolean;
}

export interface FormFieldHiddenRules {
  readonly _tag: 'hidden';
  readonly value: number;
  readonly required: boolean;
}

export interface FormFieldCheckboxRules {
  readonly _tag: 'checkbox';
  readonly required: boolean;
}

export interface FormFieldToggleRules {
  readonly _tag: 'toggle';
  readonly required: boolean;
}

export type FormFieldSelectOption =
  | {
      readonly label: string;
      readonly value: string | number;
    }
  | string;

export interface FormSection {
  readonly label: string;
  readonly description?: string;
  readonly fields: ReadonlyArray<FormField>;
}

export type Form = FormSection[];
